import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
  CircularProgress,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  OpenInFull as OpenInFullIcon,
  CloseFullscreen as CloseFullscreenIcon,
  Refresh as RefreshIcon,
  Inbox as InboxIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  Drafts as DraftsIcon,
  Star as StarIcon,
} from '@mui/icons-material';

type ImapConfig = {
  host: string;
  port: number;
  tls: 'none' | 'tls' | 'ssl';
  username: string;
  password: string;
  mailbox: string;
};

type EmailMessage = {
  id?: number;
  uid?: number;
  subject?: string;
  from?: string;
  date?: string;
  html?: string;
  text?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  imap: ImapConfig;
  callApi: (action: string, payload: any) => Promise<any>;
};

const normalizeFolderLabel = (name: string) => {
  const n = (name || '').trim();
  const low = n.toLowerCase();
  if (low === 'inbox') return { label: 'Inbox', icon: <InboxIcon fontSize="small" /> };
  if (low.includes('sent')) return { label: 'Sent', icon: <SendIcon fontSize="small" /> };
  if (low.includes('trash') || low.includes('deleted')) return { label: 'Trash', icon: <DeleteIcon fontSize="small" /> };
  if (low.includes('draft')) return { label: 'Drafts', icon: <DraftsIcon fontSize="small" /> };
  if (low.includes('star')) return { label: 'Starred', icon: <StarIcon fontSize="small" /> };
  return { label: n || 'Mailbox', icon: <InboxIcon fontSize="small" /> };
};

const EmailManagerDialog: React.FC<Props> = ({ open, onClose, imap, callApi }) => {
  const [folders, setFolders] = React.useState<string[]>([]);
  const [folderFilter, setFolderFilter] = React.useState('');
  const [selectedFolder, setSelectedFolder] = React.useState<string>('INBOX');

  const [messages, setMessages] = React.useState<EmailMessage[]>([]);
  const [selectedIndex, setSelectedIndex] = React.useState<number>(0);

  const [loadingFolders, setLoadingFolders] = React.useState(false);
  const [loadingMessages, setLoadingMessages] = React.useState(false);
  const [loadingBody, setLoadingBody] = React.useState(false);
  const [fullScreen, setFullScreen] = React.useState(false);

  const bodyCacheRef = React.useRef<Record<string, { html?: string; text?: string }>>({});

  const refreshFolders = React.useCallback(async () => {
    try {
      setLoadingFolders(true);
      const json = await callApi('imap_list_folders', { imap });
      const list: string[] = Array.isArray(json?.folders) ? json.folders : [];
      setFolders(list);

      const defaultFolder = (imap.mailbox || '').trim() || 'INBOX';
      const exists = list.some((f) => f.toLowerCase() === defaultFolder.toLowerCase());
      setSelectedFolder(exists ? defaultFolder : (list[0] || defaultFolder));
    } finally {
      setLoadingFolders(false);
    }
  }, [callApi, imap]);

  const refreshMessages = React.useCallback(async (folder: string) => {
    try {
      setLoadingMessages(true);
      const nextImap = { ...imap, mailbox: folder };
      const json = await callApi('imap_fetch', { imap: nextImap, limit: 30 });
      const list: EmailMessage[] = Array.isArray(json?.messages) ? json.messages : [];
      setMessages(list);
      setSelectedIndex(0);
    } finally {
      setLoadingMessages(false);
    }
  }, [callApi, imap]);

  const fetchBody = React.useCallback(async (m: EmailMessage | null) => {
    if (!m) return;
    const id = typeof m.id === 'number' ? m.id : 0;
    const uid = typeof m.uid === 'number' ? m.uid : 0;
    if (!id && !uid) return;

    const cacheKey = `${selectedFolder}::${uid || id}`;
    const cached = bodyCacheRef.current[cacheKey];
    if (cached && (cached.html || cached.text)) {
      setMessages((prev) =>
        prev.map((x, idx) => {
          if (idx !== selectedIndex) return x;
          return { ...x, html: cached.html ?? x.html, text: cached.text ?? x.text };
        })
      );
      return;
    }
    try {
      setLoadingBody(true);
      const json = await callApi('imap_get_message', { imap: { ...imap, mailbox: selectedFolder }, id, uid });
      const msg = json?.message;
      if (!msg || typeof msg !== 'object') return;

      const html = typeof msg.html === 'string' ? msg.html : undefined;
      const text = typeof msg.text === 'string' ? msg.text : undefined;
      bodyCacheRef.current[cacheKey] = { html, text };
      setMessages((prev) =>
        prev.map((x, idx) => {
          if (idx !== selectedIndex) return x;
          return {
            ...x,
            html: html ?? x.html,
            text: text ?? x.text,
          };
        })
      );
    } finally {
      setLoadingBody(false);
    }
  }, [callApi, imap, selectedFolder, selectedIndex]);

  React.useEffect(() => {
    if (!open) return;
    refreshFolders();
  }, [open, refreshFolders]);

  React.useEffect(() => {
    if (!open) return;
    if (!selectedFolder) return;
    refreshMessages(selectedFolder);
  }, [open, selectedFolder, refreshMessages]);

  // Intentionally do NOT auto-fetch the first email body.
  // This keeps the popup feeling instant; bodies are fetched on click.

  const filteredFolders = React.useMemo(() => {
    const q = folderFilter.trim().toLowerCase();
    if (!q) return folders;
    return folders.filter((f) => f.toLowerCase().includes(q));
  }, [folders, folderFilter]);

  const selectedMessage = messages[selectedIndex] || null;
  const selectedHtml = selectedMessage?.html || '';
  const selectedText = selectedMessage?.text || '';
  const hasBody = Boolean(selectedHtml || selectedText);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" fullScreen={fullScreen}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InboxIcon fontSize="small" />
          <Typography variant="h6" fontWeight={900}>Email Manager</Typography>
          <Chip size="small" variant="outlined" label={(imap.username || '').trim() || 'IMAP'} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => setFullScreen((p) => !p)}
            aria-label={fullScreen ? 'Exit fullscreen' : 'Fullscreen'}
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {fullScreen ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
          </IconButton>
          <IconButton onClick={onClose} aria-label="Close" sx={{ width: 44, height: 44, borderRadius: 2 }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {(loadingFolders || loadingMessages || loadingBody) && <LinearProgress />}

      <DialogContent sx={{ pt: 2 }}>
        <GridLayout>
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                value={folderFilter}
                onChange={(e) => setFolderFilter(e.target.value)}
                size="small"
                placeholder="Search folders…"
                fullWidth
              />
              <IconButton
                onClick={refreshFolders}
                disabled={loadingFolders || loadingMessages}
                aria-label="Refresh folders"
              >
                {loadingFolders ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </Box>
            <Divider />
            <List dense disablePadding sx={{ maxHeight: 520, overflow: 'auto' }}>
              {filteredFolders.length === 0 && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">No folders</Typography>
                </Box>
              )}
              {filteredFolders.map((f) => {
                const norm = normalizeFolderLabel(f);
                const active = f === selectedFolder;
                return (
                  <ListItemButton
                    key={f}
                    selected={active}
                    onClick={() => setSelectedFolder(f)}
                    sx={{ py: 1.1 }}
                  >
                    <Box sx={{ width: 24, display: 'flex', justifyContent: 'center' }}>{norm.icon}</Box>
                    <ListItemText primary={norm.label} secondary={f !== norm.label ? f : undefined} />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>

          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography fontWeight={900}>Messages</Typography>
              <IconButton
                onClick={() => refreshMessages(selectedFolder)}
                disabled={loadingFolders || loadingMessages || !selectedFolder}
                aria-label="Refresh messages"
              >
                {loadingMessages ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </Box>
            <Divider />
            <List dense disablePadding sx={{ maxHeight: 520, overflow: 'auto' }}>
              {messages.length === 0 && !loadingMessages && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">No messages</Typography>
                </Box>
              )}
              {messages.map((m, idx) => (
                <ListItemButton
                  key={idx}
                  selected={idx === selectedIndex}
                  onClick={() => {
                    setSelectedIndex(idx);
                    // fetch body on demand (fast + cached)
                    setTimeout(() => fetchBody(m), 0);
                  }}
                  sx={{ alignItems: 'flex-start', py: 1.1 }}
                >
                  <ListItemText
                    primary={m.subject || '(no subject)'}
                    secondary={
                      <span>
                        {(m.from || '-') + ' • ' + (m.date || '-')}
                      </span>
                    }
                    primaryTypographyProps={{ fontWeight: 800, fontSize: 13 }}
                    secondaryTypographyProps={{ fontSize: 12 }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>

          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ p: 1.5 }}>
              <Typography fontWeight={900}>Preview</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedMessage ? (selectedMessage.subject || '(no subject)') : 'Select an email to preview'}
              </Typography>
            </Box>
            <Divider />
            <Box sx={{ p: 2, maxHeight: 520, overflow: 'auto' }}>
              {!selectedMessage && (
                <Typography variant="body2" color="text.secondary">Nothing selected.</Typography>
              )}
              {selectedMessage && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip size="small" label={`From: ${selectedMessage.from || '-'}`} />
                    <Chip size="small" label={`Date: ${selectedMessage.date || '-'}`} />
                  </Box>
                  {loadingBody && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">Loading email…</Typography>
                    </Box>
                  )}

                  {!loadingBody && !hasBody && (
                    <Typography variant="body2" color="text.secondary">
                      This email preview is empty right now. Click the message again to retry.
                    </Typography>
                  )}

                  {!loadingBody && hasBody && (
                    <>
                      {selectedHtml ? (
                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            overflow: 'hidden',
                            bgcolor: 'background.paper',
                          }}
                        >
                          <iframe
                            title="Email preview"
                            sandbox="allow-same-origin"
                            style={{ width: '100%', height: fullScreen ? 600 : 380, border: 0 }}
                            srcDoc={`<!doctype html><html><head><meta charset="utf-8" />
<meta name="viewport" content="width=device-width,initial-scale=1" />
<base target="_blank" />
<style>body{margin:0;padding:14px;font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;font-size:14px;line-height:1.5;color:#111827} img{max-width:100%;height:auto} a{color:#2563eb}</style>
</head><body>${selectedHtml}</body></html>`}
                          />
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {selectedText}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Box>
          </Paper>
        </GridLayout>
      </DialogContent>
    </Dialog>
  );
};

const GridLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '280px 1fr 1fr' },
        gap: 2,
      }}
    >
      {children}
    </Box>
  );
};

export default EmailManagerDialog;
